import React, { Component } from 'react';
import { TerraDraw, TerraDrawPolygonMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import CustomFeeMapForm from 'src/components/maps/custom-fee-map-form';
import { asyncRequest, Entities, TOMCAT_URL } from 'src/common';

class CustomFeeMap extends Component {
    state = {
        polygonCoords: [],
        polygonError: false,
        inEditMode: false,
        editFormValues: undefined,
    };

    constructor(props) {
        super(props);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.mapRef = React.createRef();
        this.mapInstance = null;
        this.terraDraw = null;
        this.activePolygon = null;
        this.tilesLoadedListener = null;
    }

    componentDidMount() {
        this.initGoogleMap();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.activeGeofence !== this.props.activeGeofence) {
            this.drawActivePolygon();
        }
        if (prevProps.editGeofence !== this.props.editGeofence && this.props.editGeofence != null) {
            this.props.onPolygonComplete(this.props.editGeofence.boundaryList);
            this.setState({
                polygonCoords: this.props.editGeofence.boundaryList,
                inEditMode: true,
                editFormValues: {
                    ...this.props.editGeofence,
                    description: this.props.editGeofence.name,
                },
            });
            this.drawEditPolygon();
        }
        if (prevProps.editGeofence !== this.props.editGeofence && this.props.editGeofence == null) {
            this.clearMapAndForm();
        }
    }

    initGoogleMap() {
        if (!this.mapRef.current || !window.google || !window.google.maps) return;

        let mapCenter = this.props.center || { lat: 48.2082, lng: 16.3738 };
        const activeGeofence = this.props.activeGeofence;

        if (activeGeofence && activeGeofence.boundaryList && activeGeofence.boundaryList.length > 0) {
            mapCenter = {
                lat: Number(activeGeofence.boundaryList[0].lat || activeGeofence.boundaryList[0].latitude),
                lng: Number(activeGeofence.boundaryList[0].lng || activeGeofence.boundaryList[0].longitude),
            };
        }

        this.mapInstance = new window.google.maps.Map(this.mapRef.current, {
            center: mapCenter,
            zoom: this.props.zoom || 10,
        });

        const map = this.mapInstance;

        const input = document.getElementById('pac-input');
        const searchBox = new google.maps.places.SearchBox(input);

        map.controls[google.maps.ControlPosition.TOP_LEFT].push(input);
        map.addListener('bounds_changed', () => {
            searchBox.setBounds(map.getBounds());
        });

        let markers = [];

        searchBox.addListener('places_changed', () => {
            const places = searchBox.getPlaces();

            if (places.length === 0) {
                return;
            }

            markers.forEach(marker => {
                marker.setMap(null);
            });
            markers = [];

            const bounds = new google.maps.LatLngBounds();

            places.forEach(place => {
                if (!place.geometry || !place.geometry.location) {
                    console.log('Returned place contains no geometry');
                    return;
                }

                const icon = {
                    url: place.icon,
                    size: new google.maps.Size(71, 71),
                    origin: new google.maps.Point(0, 0),
                    anchor: new google.maps.Point(17, 34),
                    scaledSize: new google.maps.Size(25, 25),
                };

                markers.push(
                    new google.maps.Marker({
                        map,
                        icon,
                        title: place.name,
                        position: place.geometry.location,
                    }),
                );
                if (place.geometry.viewport) {
                    bounds.union(place.geometry.viewport);
                } else {
                    bounds.extend(place.geometry.location);
                }
            });
            map.fitBounds(bounds);
        });

        this.tilesLoadedListener = window.google.maps.event.addListenerOnce(this.mapInstance, 'tilesLoaded', () => {
            if (this.terraDraw) return;

            const mapDiv = this.mapInstance.getDiv();
            if (mapDiv && !mapDiv.id) {
                mapDiv.id = 'terra-draw-map-container';
            }

            this.terraDraw = new TerraDraw({
                adapter: new TerraDrawGoogleMapsAdapter({
                    map: this.mapInstance,
                    lib: window.google.maps,
                    coordinatePrecision: 6,
                }),
                modes: [new TerraDrawPolygonMode()],
            });

            this.terraDraw.start();
            this.terraDraw.setMode('polygon');
            this.terraDraw.on('finish', this.handlePolygonComplete);
        });

        this.drawActivePolygon();
    }

    drawActivePolygon() {
        if (this.activePolygon) {
            this.activePolygon.setMap(null);
            this.activePolygon = null;
        }

        const { activeGeofence } = this.props;
        if (!this.mapInstance || !activeGeofence || !activeGeofence.boundaryList) return;

        const bounds = new window.google.maps.LatLngBounds();
        const activePolygonPaths = activeGeofence.boundaryList.map(coord => {
            const point = {
                lat: Number(coord.lat || coord.latitude),
                lng: Number(coord.lng || coord.longitude),
            };
            bounds.extend(point);
            return point;
        });

        this.activePolygon = new window.google.maps.Polygon({
            paths: activePolygonPaths,
            fillColor: '#dc3545',
            fillOpacity: 0.35,
            strokeColor: '#dc3545',
            strokeOpacity: 0.8,
            strokeWeight: 3,
            clickable: false,
            editable: false,
            zIndex: 999,
        });

        this.activePolygon.setMap(this.mapInstance);

        if (activePolygonPaths.length > 0) {
            this.mapInstance.fitBounds(bounds);
        }
    }

    drawEditPolygon() {
        if (this.activePolygon) {
            this.activePolygon.setMap(null);
            this.activePolygon = null;
        }

        const { editGeofence } = this.props;
        if (!this.mapInstance || !editGeofence || !editGeofence.boundaryList) return;
        const bounds = new window.google.maps.LatLngBounds();
        const activePolygonPaths = editGeofence.boundaryList.map(coord => {
            const point = {
                lat: Number(coord.lat || coord.latitude),
                lng: Number(coord.lng || coord.longitude),
            };
            bounds.extend(point);
            return point;
        });

        this.activePolygon = new window.google.maps.Polygon({
            paths: activePolygonPaths,
            fillColor: '#dc3545',
            fillOpacity: 0.35,
            strokeColor: '#dc3545',
            strokeOpacity: 0.8,
            strokeWeight: 3,
            clickable: false,
            editable: false,
            zIndex: 999,
        });

        this.activePolygon.setMap(this.mapInstance);

        if (activePolygonPaths.length > 0) {
            this.mapInstance.fitBounds(bounds);
        }
    }

    handlePolygonComplete = id => {
        const snapshot = this.terraDraw.getSnapshot();
        const feature = snapshot.find(f => f.id === id);

        if (!feature || feature.geometry.type !== 'Polygon') return;

        const rawCoords = feature.geometry.coordinates[0];
        const maxPoints = this.props.maxPoints || 16;

        if (rawCoords.length - 1 > maxPoints) {
            // eslint-disable-next-line no-alert
            alert(`Maximum ${maxPoints} points allowed.`);
            this.terraDraw.removeFeatures([id]);
            return;
        }

        const featuresToRemove = snapshot.filter(f => f.id !== id).map(f => f.id);
        if (featuresToRemove.length > 0) {
            this.terraDraw.removeFeatures(featuresToRemove);
        }

        const coords = rawCoords.map(coord => ({
            lat: coord[1],
            lng: coord[0],
        }));

        this.setState({ polygonCoords: coords });

        if (this.state.polygonCoords.length > 2) this.setState({ polygonError: false });

        if (this.props.onPolygonComplete) {
            this.props.onPolygonComplete(coords);
        }
    };

    handleDeletePolygon = () => {
        if (this.terraDraw) {
            this.terraDraw.clear();
        }

        this.setState({ polygonCoords: [] });

        if (this.props.onPolygonComplete) {
            this.props.onPolygonComplete([]);
        }
    };

    handleOperatorSelect = option => {
        if (this.props.onOperatorSelect) {
            this.props.onOperatorSelect(option);
        }
    };

    async updateOrCreateGeofenceArea(data, method) {
        const { t } = this.props;
        await asyncRequest({
            method: 'POST',
            endpoint: `${TOMCAT_URL}api/${Entities.GEOFENCE_AREA.repository}/${method}`,
            body: {
                ...data,
            },
        })
            .then(response => {
                const successMessageKey =
                    method === 'update' ? 'geofence.response.success_update' : 'geofence.response.success';

                if (response) {
                    this.props.search(Entities.GEOFENCE_AREA, `get-all`, 0, undefined, true);
                }

                this.clearMapAndForm();
                this.props.addNotification(t('common_phrases.success'), t(successMessageKey), 'success', 'tr');

                this.props.removeGeofenceForEdit();
            })
            .catch(error => {
                this.props.addErrorNotification({ code: error.status, message: error.statusText });
            });
    }

    clearMapAndForm() {
        this.setState({
            polygonCoords: [],
            inEditMode: false,
            editFormValues: {},
        });
        this.drawEditPolygon();
        if (this.activePolygon) {
            this.activePolygon.setMap(null);
        }
    }

    handleSubmit(data) {
        if (this.state.polygonCoords.length < 2) {
            this.setState({ polygonError: true });
            return;
        }
        const mergedData = { ...data, boundary: this.state.polygonCoords };
        if (this.props.editGeofence) this.updateOrCreateGeofenceArea(mergedData, 'update');
        else this.updateOrCreateGeofenceArea(mergedData, 'create');
    }

    componentWillUnmount() {
        if (this.tilesLoadedListener && window.google && window.google.maps) {
            window.google.maps.event.removeListener(this.tilesLoadedListener);
        }

        if (this.activePolygon) {
            this.activePolygon.setMap(null);
        }

        if (this.terraDraw) {
            try {
                this.terraDraw.stop();
            } catch (error) {
                console.warn(error.message);
            }
        }
    }

    render() {
        const { t, busCompanies } = this.props;
        return (
            <div className="row">
                <div className="col-md-8" style={{ border: this.state.polygonError ? '2px solid red' : '' }}>
                    <input id="pac-input" className="controls" type="text" placeholder={t('common_phrases.search')} />
                    <div ref={this.mapRef} style={{ width: '100%', height: '500px' }} />
                    {this.state.polygonError && (
                        <div style={{ fontSize: 14, color: 'rgb(244, 67, 54)', marginTop: 8, textAlign: 'center' }}>
                            {t('error_missing.fill_in_valid_geo_area')}
                        </div>
                    )}
                </div>

                <div className="col-md-4">
                    <CustomFeeMapForm
                        t={t}
                        busCompanies={busCompanies}
                        onOperatorSelect={this.handleOperatorSelect}
                        onSubmit={this.handleSubmit}
                        initialValues={this.state.editFormValues}
                        handleDeletePolygon={this.handleDeletePolygon}
                    />
                </div>
            </div>
        );
    }
}

export default CustomFeeMap;
